import { useState } from 'react';
import { ArrowLeft, ShieldCheck, CreditCard, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManageSubscription() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function openPortal(e) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Veuillez saisir un email valide.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/stripe/portal-by-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          return_url: window.location.origin + '/manage-subscription',
        }),
      });
      const data = await r.json();
      if (r.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setError(data.error || "Aucun abonnement trouvé pour cet email.");
      }
    } catch (err) {
      setError('Connexion impossible. Réessayez dans quelques instants.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/"
          data-testid="manage-sub-back-link"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-10 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>

        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-[#FF6B35] text-xs font-semibold tracking-wide uppercase">
          <CreditCard size={13} />
          Espace abonnement
        </div>

        <h1 className="font-['Poppins'] font-bold text-3xl sm:text-4xl lg:text-5xl mb-5 leading-tight">
          Gérer mon abonnement
          <br />
          <span className="text-[#FF6B35]">ClayPulse Pro</span>
        </h1>

        <p className="text-[#A0A0A0] text-base sm:text-[17px] leading-relaxed mb-10">
          Modifiez votre carte bancaire, téléchargez vos factures ou résiliez
          votre abonnement à tout moment. Vous serez redirigé vers le portail
          sécurisé Stripe.
        </p>

        <form onSubmit={openPortal} className="space-y-5">
          <div>
            <label htmlFor="manage-email" className="block text-sm text-white font-medium mb-2">
              Email utilisé lors du paiement
            </label>
            <input
              id="manage-email"
              data-testid="manage-sub-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              className="w-full px-4 py-3.5 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] text-white placeholder-[#5A5A5A] focus:border-[#FF6B35] focus:outline-none transition-colors"
              disabled={loading}
            />
            <p className="text-xs text-[#6A6A6A] mt-2">
              Saisissez l'adresse email avec laquelle vous avez payé sur Stripe.
            </p>
          </div>

          {error && (
            <div
              data-testid="manage-sub-error"
              className="flex items-start gap-3 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm"
            >
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="manage-sub-submit-btn"
            disabled={loading || !email.trim()}
            className="w-full px-6 py-4 rounded-xl bg-[#FF6B35] hover:bg-[#FF7B4A] disabled:bg-[#FF6B35]/40 disabled:cursor-not-allowed text-white font-semibold text-base transition-colors flex items-center justify-center gap-2"
          >
            {loading ? 'Ouverture du portail Stripe…' : 'Ouvrir mon espace Stripe'}
          </button>
        </form>

        <div className="mt-10 pt-8 border-t border-[#1F1F1F] grid sm:grid-cols-2 gap-5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#FF6B35] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium mb-1">Portail sécurisé</p>
              <p className="text-[#7A7A7A] text-xs leading-relaxed">
                Toutes les opérations sont gérées par Stripe — leader mondial du paiement en ligne.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-[#FF6B35] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium mb-1">Résiliation immédiate</p>
              <p className="text-[#7A7A7A] text-xs leading-relaxed">
                Annulez en un clic. Vous gardez l'accès Pro jusqu'à la fin de la période payée.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-10 text-xs text-[#5A5A5A] leading-relaxed">
          Besoin d'aide ?{' '}
          <a
            href="mailto:contact@claypulse.app"
            className="text-[#FF6B35] hover:underline"
            data-testid="manage-sub-contact-link"
          >
            contact@claypulse.app
          </a>
        </p>
      </div>
    </div>
  );
}
